import { useState, useEffect } from 'react';
// NOTE: Removed motion/AnimatePresence to fix React DOM insertBefore errors
// Using CSS transitions instead for better stability
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Utensils, Plus, Calendar, Loader2, RefreshCw, AlertTriangle, Heart, Clock, ShoppingCart, ChevronDown, ChevronUp, Star, MessageSquare, ThumbsUp, ThumbsDown, X, Info, Leaf, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Ingredient Nutrition Modal Component - Uses regular divs with CSS transitions to avoid React DOM conflicts
const IngredientModal = ({ ingredient, onClose, API }) => {
  const [nutrition, setNutrition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNutrition = async () => {
      try {
        // Clean ingredient name (remove quantities like "100g", "200ml", etc.)
        const cleanName = ingredient.replace(/\d+\s*(g|ml|kg|buc|linguri?|lingurițe?|cești?)\b/gi, '').trim();
        const response = await axios.get(`${API}/ingredients/nutrition/${encodeURIComponent(cleanName)}`);
        // Store the full response - includes ingredient name and nutrition_per_100g
        setNutrition(response.data);
      } catch (err) {
        setError('Nu am găsit informații pentru acest ingredient');
      } finally {
        setLoading(false);
      }
    };
    fetchNutrition();
  }, [ingredient, API]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl text-[#1A4D2E]">Informații Nutriționale</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <p className="text-[#4a5568]">{error}</p>
            </div>
          ) : nutrition && (
            <div className="space-y-4">
              {/* Header */}
              <div className="text-center pb-4 border-b">
                <h4 className="text-lg font-medium text-[#1A4D2E] capitalize">{nutrition.ingredient || nutrition.matched || ingredient}</h4>
                {nutrition.nutrition_per_100g?.category && (
                  <Badge className="mt-2 bg-[#1A4D2E]/10 text-[#1A4D2E]">{nutrition.nutrition_per_100g.category}</Badge>
                )}
                <p className="text-sm text-[#4a5568] mt-2">per 100g</p>
              </div>

              {/* Calories */}
              <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2d7a4a] text-white p-4 rounded-xl text-center">
                <p className="text-3xl font-bold">{nutrition.nutrition_per_100g?.calories || 0}</p>
                <p className="text-sm opacity-80">kilocalorii</p>
              </div>

              {/* Macros */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-blue-50 p-3 rounded-xl text-center">
                  <p className="text-lg font-bold text-blue-700">{nutrition.nutrition_per_100g?.protein || 0}g</p>
                  <p className="text-xs text-blue-600">Proteine</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-xl text-center">
                  <p className="text-lg font-bold text-amber-700">{nutrition.nutrition_per_100g?.carbs || 0}g</p>
                  <p className="text-xs text-amber-600">Carbohidrați</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-xl text-center">
                  <p className="text-lg font-bold text-purple-700">{nutrition.nutrition_per_100g?.fat || 0}g</p>
                  <p className="text-xs text-purple-600">Grăsimi</p>
                </div>
                <div className="bg-green-50 p-3 rounded-xl text-center">
                  <p className="text-lg font-bold text-green-700">{nutrition.nutrition_per_100g?.fiber || 0}g</p>
                  <p className="text-xs text-green-600">Fibre</p>
                </div>
              </div>

              {/* Source */}
              {nutrition.source && (
                <div className="text-center text-xs text-[#4a5568] pt-2 border-t">
                  Sursă: {nutrition.source}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MealPlans = () => {
  const { API } = useAuth();
  const [mealPlans, setMealPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingQuick, setGeneratingQuick] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [days, setDays] = useState('7');
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [parsedPlanData, setParsedPlanData] = useState(null);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  
  // Feedback states
  const [feedbackPrompt, setFeedbackPrompt] = useState(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedback, setFeedback] = useState({
    overall_rating: 3,
    followed_plan_percentage: 70,
    liked_meals: [],
    disliked_meals: [],
    foods_to_add: [],
    foods_to_remove: [],
    difficulty_level: 'ok',
    portion_feedback: 'ok',
    comments: ''
  });
  const [newFoodToAdd, setNewFoodToAdd] = useState('');
  const [newFoodToRemove, setNewFoodToRemove] = useState('');
  const [userPreferences, setUserPreferences] = useState(null);

  useEffect(() => {
    fetchActivePlan();
    fetchMealPlans();
    checkFeedbackNeeded();
    fetchUserPreferences();
  }, []);

  const fetchActivePlan = async () => {
    try {
      const response = await axios.get(`${API}/meal-plans/current/active`);
      setActivePlan(response.data);
      
      // Parse the meal plan data - it's now directly in meal_plan, not meal_plan.ai_response
      if (response.data?.meal_plan) {
        const planData = response.data.meal_plan;
        // If it's already an object with days, use it directly
        if (planData.days) {
          setParsedPlanData(planData);
        } else {
          // Try to parse if it's a string (legacy format)
          const parsed = parseAIResponse(planData);
          setParsedPlanData(parsed);
        }
      } else if (response.data?.active_plan?.ai_response) {
        // Fallback to legacy format
        const parsed = parseAIResponse(response.data.active_plan.ai_response);
        setParsedPlanData(parsed);
      }
    } catch (error) {
      console.error('Error fetching active plan:', error);
    }
  };
  
  const checkFeedbackNeeded = async () => {
    try {
      const response = await axios.get(`${API}/meal-plans/weekly-check`);
      setFeedbackPrompt(response.data);
    } catch (error) {
      console.error('Error checking feedback:', error);
    }
  };
  
  const fetchUserPreferences = async () => {
    try {
      const response = await axios.get(`${API}/meal-plans/preferences`);
      setUserPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };
  
  const submitFeedback = async () => {
    if (!feedbackPrompt?.meal_plan_id) return;
    
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/meal-plans/feedback`, {
        meal_plan_id: feedbackPrompt.meal_plan_id,
        ...feedback
      });
      toast.success('Mulțumim pentru feedback! Următorul plan va fi adaptat preferințelor tale.');
      setFeedbackPrompt({ ...feedbackPrompt, needs_feedback: false });
      fetchUserPreferences();
    } catch (error) {
      toast.error('Eroare la trimiterea feedback-ului');
    } finally {
      setSubmittingFeedback(false);
    }
  };
  
  const addFoodToAdd = () => {
    if (newFoodToAdd.trim() && !feedback.foods_to_add.includes(newFoodToAdd.trim())) {
      setFeedback({ ...feedback, foods_to_add: [...feedback.foods_to_add, newFoodToAdd.trim()] });
      setNewFoodToAdd('');
    }
  };
  
  const addFoodToRemove = () => {
    if (newFoodToRemove.trim() && !feedback.foods_to_remove.includes(newFoodToRemove.trim())) {
      setFeedback({ ...feedback, foods_to_remove: [...feedback.foods_to_remove, newFoodToRemove.trim()] });
      setNewFoodToRemove('');
    }
  };

  const fetchMealPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/meal-plans`);
      setMealPlans(response.data || []);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const parseAIResponse = (aiResponse) => {
    try {
      if (typeof aiResponse === 'object') return aiResponse;
      
      // Try to find JSON in the response
      const start = aiResponse.indexOf('{');
      const end = aiResponse.lastIndexOf('}') + 1;
      if (start !== -1 && end > start) {
        const jsonStr = aiResponse.substring(start, end);
        return JSON.parse(jsonStr);
      }
    } catch (e) {
      console.error('Error parsing AI response:', e);
    }
    return null;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);

    try {
      const response = await axios.post(`${API}/meal-plans/generate`, {
        days: parseInt(days),
      });

      toast.success('Plan de mese generat cu succes! 🥗');
      await fetchActivePlan();
      await fetchMealPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la generarea planului');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateQuick = async () => {
    setGeneratingQuick(true);

    try {
      await axios.post(`${API}/meal-plans/generate-quick`, {
        days: parseInt(days),
      });

      toast.success('Plan generat instant din rețete sănătoase! 🚀');
      await fetchActivePlan();
      await fetchMealPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la generarea planului');
    } finally {
      setGeneratingQuick(false);
    }
  };

  const handleRenew = async () => {
    setRenewing(true);
    try {
      const response = await axios.post(`${API}/meal-plans/renew`);
      toast.success(response.data.message || 'Plan reînnoit cu succes!');
      
      // Show health alerts if any
      if (response.data.health_alerts?.length > 0) {
        response.data.health_alerts.forEach(alert => {
          toast.info(alert.message, { duration: 5000 });
        });
      }
      
      await fetchActivePlan();
      await fetchMealPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la reînnoirea planului');
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8 max-w-7xl" data-testid="meal-plans-page">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-6 md:mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
              Planuri de Mese
            </h1>
            <p className="text-[#4a5568] text-base md:text-lg">
              Planuri personalizate adaptate condițiilor tale medicale
            </p>
          </div>

          {/* Feedback Alert Banner */}
          {feedbackPrompt?.needs_feedback && (
            <div
              className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Evaluare săptămânală disponibilă!</p>
                    <p className="text-sm text-amber-600">{feedbackPrompt.message}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => document.getElementById('feedback-tab')?.click()}
                  className="bg-amber-600 text-white rounded-full hover:bg-amber-700"
                >
                  Oferă Feedback
                </Button>
              </div>
            </div>
          )}

          <Tabs defaultValue="active" className="space-y-4 sm:space-y-6">
            <TabsList className="bg-white p-1 rounded-xl flex flex-wrap gap-1 h-auto">
              <TabsTrigger value="active" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white text-xs sm:text-sm flex-1 sm:flex-none py-2">
                Plan Activ
              </TabsTrigger>
              <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white text-xs sm:text-sm flex-1 sm:flex-none py-2">
                Istoric
              </TabsTrigger>
              <TabsTrigger value="generate" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white text-xs sm:text-sm flex-1 sm:flex-none py-2">
                Generează
              </TabsTrigger>
              <TabsTrigger 
                id="feedback-tab"
                value="feedback" 
                className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white text-xs sm:text-sm flex-1 sm:flex-none py-2"
              >
                <MessageSquare className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Feedback</span>
                {feedbackPrompt?.needs_feedback && (
                  <span className="ml-1 w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                )}
              </TabsTrigger>
            </TabsList>

            {/* Active Plan Tab */}
            <TabsContent value="active" className="space-y-4 sm:space-y-6">
              {activePlan?.has_active_plan ? (
                <>
                  {/* Status Card */}
                  <Card className="border-none card-shadow">
                    <CardContent className="p-4 sm:pt-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center flex-shrink-0 ${activePlan.needs_renewal ? 'bg-amber-100' : 'bg-green-100'}`}>
                            {activePlan.needs_renewal ? (
                              <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
                            ) : (
                              <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-serif text-lg sm:text-xl text-[#1A4D2E]">
                              {activePlan.needs_renewal ? 'Planul a expirat' : 'Plan Activ'}
                            </h3>
                            <p className="text-[#4a5568] text-sm">{activePlan.message}</p>
                            {!activePlan.needs_renewal && (
                              <div className="flex items-center gap-2 mt-1 text-xs sm:text-sm text-[#4a5568]">
                                <Clock className="w-4 h-4" />
                                <span>{activePlan.days_remaining} zile rămase</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          onClick={handleRenew}
                          disabled={renewing}
                          className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] w-full sm:w-auto"
                          data-testid="renew-plan-button"
                        >
                          {renewing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Reînnoiește Plan
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Health Alerts */}
                  {activePlan.meal_plan?.health_alerts?.length > 0 && (
                    <Card className="border-none card-shadow border-l-4 border-l-amber-500">
                      <CardHeader>
                        <CardTitle className="font-serif text-lg text-[#1A4D2E] flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                          Alerte de Sănătate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {activePlan.meal_plan.health_alerts.map((alert, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-amber-800">{alert.message}</p>
                                <p className="text-xs text-amber-600 mt-1">
                                  Valoare: {alert.value} | Ajustare: {alert.dietary_adjustment}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Medical Conditions */}
                  {activePlan.meal_plan?.medical_conditions?.length > 0 && (
                    <Card className="border-none card-shadow">
                      <CardHeader>
                        <CardTitle className="font-serif text-lg text-[#1A4D2E]">
                          Condiții Medicale Luate în Considerare
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {activePlan.meal_plan.medical_conditions.map((condition, index) => (
                            <Badge key={index} className="bg-[#1A4D2E]/10 text-[#1A4D2E]">
                              {condition}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Parsed Plan Content */}
                  {parsedPlanData && (
                    <Card className="border-none card-shadow">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="font-serif text-xl text-[#1A4D2E]">
                              Plan Personalizat - Săptămâna {parsedPlanData.week_of}
                            </CardTitle>
                            {parsedPlanData.medical_notes && (
                              <CardDescription className="mt-2 text-[#4a5568]">
                                {parsedPlanData.medical_notes}
                              </CardDescription>
                            )}
                          </div>
                          {parsedPlanData.daily_targets && (
                            <div className="hidden md:flex flex-wrap gap-2">
                              <Badge className="bg-blue-100 text-blue-800">{parsedPlanData.daily_targets.calories} kcal/zi</Badge>
                              <Badge className="bg-green-100 text-green-800">{parsedPlanData.daily_targets.protein_g}g proteină</Badge>
                              <Badge className="bg-amber-100 text-amber-800">{parsedPlanData.daily_targets.fiber_g}g fibre</Badge>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {parsedPlanData.days?.map((day, dayIndex) => (
                          <div key={dayIndex} className="mb-6 last:mb-0">
                            <div 
                              className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-xl cursor-pointer"
                              onClick={() => setExpandedPlan(expandedPlan === dayIndex ? null : dayIndex)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#1A4D2E] text-white flex items-center justify-center font-bold">
                                  {day.day}
                                </div>
                                <span className="font-medium text-[#1A4D2E]">{day.day_name}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                {day.daily_totals && (
                                  <span className="text-sm text-[#4a5568] hidden sm:block">
                                    {day.daily_totals.calories} kcal
                                  </span>
                                )}
                                {expandedPlan === dayIndex ? (
                                  <ChevronUp className="w-5 h-5 text-[#4a5568]" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-[#4a5568]" />
                                )}
                              </div>
                            </div>
                            
                            {expandedPlan === dayIndex && (
                              <div
                                className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200"
                              >
                                {day.meals?.map((meal, mealIndex) => (
                                  <div key={mealIndex} className="p-4 bg-white rounded-xl border border-gray-100">
                                    <div className="flex items-start justify-between mb-2">
                                      <div>
                                        <Badge className="bg-[#9BCF53] text-white text-xs mb-2">
                                          {meal.type?.replace('_', ' ').toUpperCase()}
                                        </Badge>
                                        <h4 className="font-medium text-[#1A4D2E]">{meal.name}</h4>
                                        <p className="text-sm text-[#4a5568]">{meal.description}</p>
                                      </div>
                                      {meal.preparation_time && (
                                        <span className="text-xs text-[#4a5568] flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          {meal.preparation_time}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {meal.ingredients && (
                                      <div className="mt-3">
                                        <p className="text-xs font-medium text-[#4a5568] mb-1">Ingrediente (click pentru detalii):</p>
                                        <div className="flex flex-wrap gap-1">
                                          {meal.ingredients.map((ing, i) => (
                                            <button
                                              key={i}
                                              onClick={() => setSelectedIngredient(ing)}
                                              className="text-xs bg-[#F5F5F0] px-2 py-1 rounded hover:bg-[#1A4D2E]/10 hover:text-[#1A4D2E] transition-colors cursor-pointer flex items-center gap-1"
                                              data-testid={`ingredient-${i}`}
                                            >
                                              <Info className="w-3 h-3 opacity-50" />
                                              {ing}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {meal.nutrition && (
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                          {meal.nutrition.calories} kcal
                                        </span>
                                        <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                                          P: {meal.nutrition.protein}g
                                        </span>
                                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">
                                          C: {meal.nutrition.carbs}g
                                        </span>
                                        <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                                          G: {meal.nutrition.fat}g
                                        </span>
                                      </div>
                                    )}
                                    
                                    {meal.medical_benefit && (
                                      <div className="mt-3 p-2 bg-green-50 rounded-lg">
                                        <p className="text-xs text-green-700">
                                          <Heart className="w-3 h-3 inline mr-1" />
                                          {meal.medical_benefit}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Weekly Shopping List */}
                        {parsedPlanData.weekly_shopping_list?.length > 0 && (
                          <div className="mt-6 p-4 bg-[#1A4D2E]/5 rounded-xl">
                            <h4 className="font-medium text-[#1A4D2E] flex items-center gap-2 mb-3">
                              <ShoppingCart className="w-5 h-5" />
                              Lista de Cumpărături
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {parsedPlanData.weekly_shopping_list.map((item, i) => (
                                <span key={i} className="text-sm bg-white px-3 py-1 rounded-full">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Medical Recommendations */}
                        {parsedPlanData.medical_recommendations?.length > 0 && (
                          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                            <h4 className="font-medium text-blue-800 mb-3">Recomandări Medicale</h4>
                            <ul className="space-y-2">
                              {parsedPlanData.medical_recommendations.map((rec, i) => (
                                <li key={i} className="text-sm text-blue-700 flex items-start gap-2">
                                  <span className="text-blue-500">•</span>
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Raw response fallback - only if parsing failed and we have the raw active_plan */}
                  {!parsedPlanData && activePlan.active_plan?.ai_response && typeof activePlan.active_plan.ai_response === 'string' && (
                    <Card className="border-none card-shadow">
                      <CardHeader>
                        <CardTitle className="font-serif text-xl text-[#1A4D2E]">Plan de Mese</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[#4a5568]">
                          {activePlan.active_plan.ai_response}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="text-center py-12">
                    <Utensils className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-[#1A4D2E] mb-2">
                      {activePlan?.message || 'Nu ai un plan de mese activ'}
                    </h3>
                    <p className="text-[#4a5568] mb-6">
                      Generează primul tău plan de mese personalizat bazat pe profilul tău medical
                    </p>
                    <Button
                      onClick={handleRenew}
                      disabled={renewing}
                      className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                    >
                      {renewing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Generează Plan Personalizat
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history">
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-[#1A4D2E] animate-spin mx-auto" />
                </div>
              ) : mealPlans.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {mealPlans.map((plan, index) => (
                    <div
                      key={plan.id}
                      className="animate-in fade-in slide-in-from-bottom-4 duration-500"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <Card
                        className="border-none card-shadow hover:shadow-lg transition-all duration-300"
                        data-testid={`meal-plan-card-${index}`}
                      >
                        <CardHeader>
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="bg-[#9BCF53] text-white">{plan.days} zile</Badge>
                            {plan.auto_renewed && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Auto-reînnoit</Badge>
                            )}
                          </div>
                          <CardTitle className="font-serif text-xl text-[#1A4D2E]">
                            Plan {plan.medical_conditions?.length > 0 ? 'Medical' : 'Personalizat'}
                          </CardTitle>
                          <CardDescription className="flex items-center space-x-2 text-sm">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(plan.created_at).toLocaleDateString('ro-RO')}</span>
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {plan.medical_conditions?.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-1">
                              {plan.medical_conditions.map((cond, i) => (
                                <Badge key={i} className="bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs">
                                  {cond}
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          {plan.health_alerts?.length > 0 && (
                            <div className="mb-3 p-2 bg-amber-50 rounded-lg">
                              <p className="text-xs text-amber-700">
                                <AlertTriangle className="w-3 h-3 inline mr-1" />
                                {plan.health_alerts.length} alerte de sănătate
                              </p>
                            </div>
                          )}
                          
                          <div className="bg-[#F5F5F0] p-4 rounded-xl">
                            <p className="text-sm text-[#4a5568] line-clamp-3">
                              {typeof plan.ai_response === 'string' 
                                ? plan.ai_response.substring(0, 150) + '...'
                                : 'Plan de mese personalizat generat de AI'}
                            </p>
                          </div>
                          
                          {plan.preferences && plan.preferences.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {plan.preferences.slice(0, 3).map((pref, i) => (
                                <Badge key={i} className="bg-gray-100 text-gray-600 text-xs">
                                  {pref}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="text-center py-12">
                    <Calendar className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-[#1A4D2E] mb-2">Niciun plan în istoric</h3>
                    <p className="text-[#4a5568]">
                      Planurile tale generate vor apărea aici
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Generate Tab */}
            <TabsContent value="generate">
              <Card className="border-none card-shadow">
                <CardHeader>
                  <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                    <Plus className="w-6 h-6 mr-2" />
                    Generează Plan Nou
                  </CardTitle>
                  <CardDescription>
                    Creează un plan de mese personalizat bazat pe profilul și condițiile tale medicale.
                    Planul va fi adaptat automat la afecțiunile tale (diabet, hipertensiune, etc.) și 
                    va include rețete recomandate de ghidurile medicale internaționale.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGenerate} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="days">Număr de zile</Label>
                      <Input
                        id="days"
                        data-testid="meal-plan-days-input"
                        type="number"
                        min="1"
                        max="30"
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                        className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12 max-w-xs"
                      />
                      <p className="text-sm text-[#4a5568]">
                        Recomandăm 7 zile pentru un plan săptămânal complet
                      </p>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <h4 className="font-medium text-blue-800 mb-2">Ce include planul:</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• 5 mese pe zi: mic dejun, prânz, cină, 2 gustări</li>
                        <li>• Rețete adaptate condițiilor tale medicale</li>
                        <li>• Valori nutriționale calculate pentru fiecare masă</li>
                        <li>• Listă de cumpărături săptămânală</li>
                        <li>• Recomandări medicale personalizate</li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        type="submit"
                        disabled={generating || generatingQuick}
                        data-testid="generate-meal-plan-button"
                        className="flex-1 bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12 px-6"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Se generează...
                          </>
                        ) : (
                          <>
                            <Utensils className="w-4 h-4 mr-2" />
                            Generează Plan Personalizat
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-4">
                      <div className="flex items-center gap-2 text-emerald-700 mb-2">
                        <Utensils className="w-5 h-5" />
                        <span className="font-medium">Generare din Baza de Date de Rețete</span>
                      </div>
                      <p className="text-sm text-emerald-600">
                        Planurile sunt generate din peste <strong>40 de rețete</strong> românești sănătoase, 
                        cu referințe științifice și personalizate pentru afecțiunile tale medicale.
                        <br />
                        <span className="text-xs opacity-75">💚 Fără costuri suplimentare • ⚡ Instant • 🔬 Bazat pe studii științifice</span>
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Feedback Tab */}
            <TabsContent value="feedback" className="space-y-6">
              {/* User Preferences Summary */}
              {userPreferences && (userPreferences.favorite_ingredients?.length > 0 || userPreferences.disliked_ingredients?.length > 0) && (
                <Card className="border-none card-shadow">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl text-[#1A4D2E]">
                      Preferințele Tale Acumulate
                    </CardTitle>
                    <CardDescription>
                      Acestea sunt luate în calcul la generarea planurilor
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {userPreferences.favorite_ingredients?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-[#4a5568] mb-2 flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4 text-green-500" />
                          Ingrediente favorite:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {userPreferences.favorite_ingredients.map((ing, i) => (
                            <Badge key={i} className="bg-green-100 text-green-700">{ing}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {userPreferences.disliked_ingredients?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-[#4a5568] mb-2 flex items-center gap-2">
                          <ThumbsDown className="w-4 h-4 text-red-500" />
                          Ingrediente de evitat:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {userPreferences.disliked_ingredients.map((ing, i) => (
                            <Badge key={i} className="bg-red-100 text-red-700">{ing}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="pt-2 border-t flex items-center gap-4 text-sm text-[#4a5568]">
                      <span>Feedback-uri oferite: {userPreferences.feedback_count || 0}</span>
                      {userPreferences.average_rating > 0 && (
                        <span className="flex items-center gap-1">
                          Rating mediu: 
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          {userPreferences.average_rating}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Feedback Form */}
              {feedbackPrompt?.needs_feedback ? (
                <Card className="border-none card-shadow">
                  <CardHeader>
                    <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center gap-2">
                      <MessageSquare className="w-6 h-6" />
                      Evaluare Săptămânală
                    </CardTitle>
                    <CardDescription>
                      Ajută-ne să îți personalizăm mai bine planul alimentar. Feedback-ul tău va fi folosit pentru a genera planuri mai bune în viitor.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Overall Rating */}
                    <div className="space-y-3">
                      <Label className="text-base">Cum a fost planul alimentar în general?</Label>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFeedback({ ...feedback, overall_rating: star })}
                            className="p-1 hover:scale-110 transition-transform"
                          >
                            <Star
                              className={`w-8 h-8 ${star <= feedback.overall_rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`}
                            />
                          </button>
                        ))}
                        <span className="ml-2 text-sm text-[#4a5568]">
                          {feedback.overall_rating === 1 && 'Nesatisfăcător'}
                          {feedback.overall_rating === 2 && 'Acceptabil'}
                          {feedback.overall_rating === 3 && 'Bun'}
                          {feedback.overall_rating === 4 && 'Foarte bun'}
                          {feedback.overall_rating === 5 && 'Excelent'}
                        </span>
                      </div>
                    </div>

                    {/* Followed Percentage */}
                    <div className="space-y-3">
                      <Label className="text-base">Ce procent din plan ai urmat? ({feedback.followed_plan_percentage}%)</Label>
                      <Slider
                        value={[feedback.followed_plan_percentage]}
                        onValueChange={(v) => setFeedback({ ...feedback, followed_plan_percentage: v[0] })}
                        max={100}
                        step={10}
                        className="w-full max-w-md"
                      />
                    </div>

                    {/* Difficulty Level */}
                    <div className="space-y-3">
                      <Label className="text-base">Cum ți s-au părut rețetele ca dificultate?</Label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'prea_greu', label: 'Prea complicate' },
                          { value: 'ok', label: 'Nivelul potrivit' },
                          { value: 'prea_simplu', label: 'Aș vrea mai elaborate' }
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFeedback({ ...feedback, difficulty_level: opt.value })}
                            className={`px-4 py-2 rounded-full border transition-colors ${
                              feedback.difficulty_level === opt.value
                                ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                                : 'bg-white text-[#4a5568] border-gray-200 hover:border-[#1A4D2E]'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Portion Feedback */}
                    <div className="space-y-3">
                      <Label className="text-base">Cum au fost porțiile?</Label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'prea_mult', label: 'Prea mari' },
                          { value: 'ok', label: 'Potrivite' },
                          { value: 'prea_putin', label: 'Prea mici' }
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFeedback({ ...feedback, portion_feedback: opt.value })}
                            className={`px-4 py-2 rounded-full border transition-colors ${
                              feedback.portion_feedback === opt.value
                                ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                                : 'bg-white text-[#4a5568] border-gray-200 hover:border-[#1A4D2E]'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Foods to Add */}
                    <div className="space-y-3">
                      <Label className="text-base flex items-center gap-2">
                        <ThumbsUp className="w-4 h-4 text-green-500" />
                        Ce alimente ai vrea să adaugăm în planurile viitoare?
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={newFoodToAdd}
                          onChange={(e) => setNewFoodToAdd(e.target.value)}
                          placeholder="Ex: avocado, somon, quinoa..."
                          className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFoodToAdd())}
                        />
                        <Button type="button" onClick={addFoodToAdd} variant="outline" className="rounded-lg">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {feedback.foods_to_add.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {feedback.foods_to_add.map((food, i) => (
                            <Badge key={i} className="bg-green-100 text-green-700 flex items-center gap-1">
                              {food}
                              <button onClick={() => setFeedback({ ...feedback, foods_to_add: feedback.foods_to_add.filter((_, idx) => idx !== i) })}>
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Foods to Remove */}
                    <div className="space-y-3">
                      <Label className="text-base flex items-center gap-2">
                        <ThumbsDown className="w-4 h-4 text-red-500" />
                        Ce alimente vrei să eliminăm definitiv?
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={newFoodToRemove}
                          onChange={(e) => setNewFoodToRemove(e.target.value)}
                          placeholder="Ex: broccoli, ficat, varză..."
                          className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFoodToRemove())}
                        />
                        <Button type="button" onClick={addFoodToRemove} variant="outline" className="rounded-lg">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {feedback.foods_to_remove.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {feedback.foods_to_remove.map((food, i) => (
                            <Badge key={i} className="bg-red-100 text-red-700 flex items-center gap-1">
                              {food}
                              <button onClick={() => setFeedback({ ...feedback, foods_to_remove: feedback.foods_to_remove.filter((_, idx) => idx !== i) })}>
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Comments */}
                    <div className="space-y-3">
                      <Label className="text-base">Alte comentarii sau sugestii?</Label>
                      <Textarea
                        value={feedback.comments}
                        onChange={(e) => setFeedback({ ...feedback, comments: e.target.value })}
                        placeholder="Spune-ne ce ai vrea să îmbunătățim..."
                        className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg min-h-[100px]"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={submitFeedback}
                      disabled={submittingFeedback}
                      className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12 px-8"
                    >
                      {submittingFeedback ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Se trimite...
                        </>
                      ) : (
                        <>
                          <Heart className="w-4 h-4 mr-2" />
                          Trimite Feedback
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="text-center py-12">
                    <MessageSquare className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-[#1A4D2E] mb-2">
                      {feedbackPrompt?.message || 'Nu ai feedback de oferit momentan'}
                    </h3>
                    <p className="text-[#4a5568] mb-4">
                      Vei putea oferi feedback după ce urmezi un plan alimentar pentru cel puțin 5 zile.
                    </p>
                    {feedbackPrompt?.days_until_feedback && (
                      <Badge className="bg-[#1A4D2E]/10 text-[#1A4D2E]">
                        Mai ai {feedbackPrompt.days_until_feedback} zile până la evaluare
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Ingredient Nutrition Modal - Uses CSS animations, no AnimatePresence */}
      {selectedIngredient && (
        <IngredientModal
          ingredient={selectedIngredient}
          onClose={() => setSelectedIngredient(null)}
          API={API}
        />
      )}
    </div>
  );
};

export default MealPlans;